import React from "react";
import { Box, Typography, Chip, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import Avatar from "./Avatar";

export interface FieldConfig {
  name: string;
  label: string;
  value: any;
  fieldType?: string;
  chipFormat?: (chip: any) => string;
}

interface DynamicFormProps {
  fields: FieldConfig[];
  initialData: Record<string, any>;
  onEdit?: () => void;
  section?: { label: string; tableName: string };
  onUpdate?: (fieldName: string, value: string) => Promise<void>;
}

const StyledBox = styled(Box)(({ theme }) => ({
  "& .MuiTypography-root": {
    color: theme.palette.text.primary,
  },
}));

const DynamicForm: React.FC<DynamicFormProps> = ({
  fields,
  initialData,
  section,
  onEdit,
}) => {
  const theme = useTheme();

  const renderField = (field: FieldConfig) => {
    switch (field.fieldType) {
      case "avatar":
        return (
          <Avatar
            src={field.value || "/default-avatar.png"}
            alt={"Click to edit"}
            size={80}
          />
        );
      case "chip":
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {initialData[field.name]?.map((chip: any) => (
              <Chip
                key={chip.id || `${chip.county}-${chip.state_code}`}
                label={field.chipFormat ? field.chipFormat(chip) : chip}
                size="small"
              />
            ))}
          </Box>
        );
      default:
        return (
          <Typography variant="body2" color="text.primary">
            {initialData[field.name] != null
              ? String(initialData[field.name])
              : ""}
          </Typography>
        );
    }
  };

  return (
    <Paper elevation={0} sx={{ mb: 3 }}>
      {section && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            p: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {section.label}
          </Typography>
        </Box>
      )}

      <StyledBox>
        {fields.map((field) => (
          <Box
            key={field.name}
            onClick={onEdit}
            sx={{
              display: "flex",
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              "&:last-child": {
                borderBottom: "none",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                minWidth: 150,
                fontWeight: "medium",
              }}
            >
              {field.label}:
            </Typography>
            <Box sx={{ flex: 1 }}>{renderField(field)}</Box>
          </Box>
        ))}
      </StyledBox>
    </Paper>
  );
};

export default DynamicForm;
